/**
 * WorkshopRightRail — the right-rail of the workshop detail page. The Crew
 * section (BON-200) ships as a live {@link CrewSlotEditor}, the Audit log
 * (BON-201) as a live {@link WorkshopAuditLog}, and the Danger zone (BON-202) as
 * a live {@link WorkshopDangerZone} cancel flow.
 *
 * Crew is editable whenever the caller has write scope; read-only for the
 * Teacher persona (always read-only) or outside the caller's region.
 *
 * Danger-zone gate: the zone renders ONLY when the caller can edit
 * (so Teacher — always `readOnly` — never sees it) and the workshop is
 * published or cancelled. Active offers disable + cancel, disabled offers
 * re-enable, cancelled shows its banner. Drafts get no danger zone (drafts are
 * discarded, not cancelled).
 *
 * The Bookings roster (BON-199) lives in the LEFT column under Basics, not the
 * rail, so its coming-soon panel is gone.
 */
import type { CrewSlotInput, WorkshopDetail } from '../../../../api/workshops';
import { useReplaceWorkshopStaff } from '../useWorkshop';
import { isPublished } from '../workshop-lifecycle';

import { CrewSlotEditor } from './CrewSlotEditor';
import { WorkshopAuditLog } from './WorkshopAuditLog';
import { WorkshopDangerZone } from './WorkshopDangerZone';

export interface WorkshopRightRailProps {
  workshop: WorkshopDetail;
  /** True for the Teacher persona or a read-only shell scope. */
  readOnly: boolean;
}

export function WorkshopRightRail({ workshop, readOnly }: WorkshopRightRailProps) {
  const replace = useReplaceWorkshopStaff(workshop.id);

  const showDangerZone =
    !readOnly && (isPublished(workshop.lifecycle) || workshop.lifecycle === 'cancelled');

  const onSave = async (staff: CrewSlotInput[]) => {
    await replace.mutateAsync(staff);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <CrewSlotEditor
        staff={workshop.staff}
        regionId={workshop.venueRegionId}
        readOnly={readOnly}
        published={isPublished(workshop.lifecycle)}
        onSave={onSave}
        saving={replace.isPending}
      />
      <WorkshopAuditLog
        workshopId={workshop.id}
        audit={workshop.audit}
        total={workshop.audit?.length ?? 0}
      />
      {showDangerZone && <WorkshopDangerZone workshop={workshop} />}
    </div>
  );
}
